import './App.scss';

import React, {createContext, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { v4 as uuidv4 } from 'uuid';
import {onAuthStateChanged, signOut as firebaseSignOut} from 'firebase/auth';

import Cookies from 'js-cookie';

import Header from './containers/Header';
import Body from './containers/Body';
import Footer from './containers/Footer';
import LoginWallpaper from './containers/LoginWallpaper';
import EmailLinkPage from './containers/EmailLinkPage';
import ErrorList from './components/ErrorList';
import {
	ALL_PROFILE,
	filterCategoriesWithLinksForProfile,
	filterLinksByProfile,
	getCategoryIdxForProfile,
	normalizeCategory,
	normalizeLink,
	normalizeProfile,
	sortCategories,
	sortProfiles,
	VIRTUAL_ALL_PROFILE_ID,
} from "./tools";
import {auth} from "./firebase";
import {
	BUNDLED_DEFAULT_AVATAR,
	clearEmailLinkUrlIfPresent,
	completeEmailLinkSignIn,
	deleteCurrentUserAccount,
	isCurrentUrlEmailSignInLink,
	isExtensionEmailLinkUrl,
	isExtensionShortSignInUrl,
	isShortSignInUrl,
	logout,
	migrateUserStorage,
	removeUserAvatar,
	requestEmailAddressChange,
	requestEmailSignInLink,
	resendVerificationEmail,
	selectUserAvatar,
	submitBugReport,
	subscribeCategories,
	subscribeLinks,
	subscribeProfiles,
	uploadUserAvatar
} from "./firebaseTools";

const AddLink = React.lazy(() => import('./components/AddLink'));
const ProfileManager = React.lazy(() => import('./components/ProfileManager'));
const AccountSettings = React.lazy(() => import('./components/AccountSettings'));
const ReportIssue = React.lazy(() => import('./components/ReportIssue'));
const BrowserExtensionGuide = React.lazy(() => import('./components/BrowserExtensionGuide'));

export const StateContext = createContext(null);
const AUTH_SESSION_COOKIE = 'authSession';
const APP_ENV = process.env.REACT_APP_ENV ?? 'localTest';
const IS_PROD_ENV = APP_ENV === 'prod';
const SESSION_DAYS = APP_ENV === 'localTest' ? 1 : 7;
const SECURE_COOKIES = APP_ENV !== 'localTest';
const DEFAULT_INFO_POPUP_TIMEOUT_MS = 10000;
const INFO_POPUP_PREF_STORAGE_PREFIX = 'linkspace.infoPopupPreference';

const createInfoPopupPreference = (keepUntilDismissed = false) => ({
	keepUntilDismissed,
	autoDismissMs: DEFAULT_INFO_POPUP_TIMEOUT_MS
});

const getInfoPopupPreferenceStorageKey = (uid) => `${INFO_POPUP_PREF_STORAGE_PREFIX}.${uid}`;

const readInfoPopupPreference = (uid) => {
	if (!uid) return createInfoPopupPreference();
	try {
		const raw = globalThis.window.localStorage.getItem(getInfoPopupPreferenceStorageKey(uid));
		if (!raw) return createInfoPopupPreference();
		const parsed = JSON.parse(raw);
		return createInfoPopupPreference(Boolean(parsed?.keepUntilDismissed));
	} catch {
		return createInfoPopupPreference();
	}
};

const persistInfoPopupPreference = (uid, preference) => {
	if (!uid) return;
	try {
		globalThis.window.localStorage.setItem(
			getInfoPopupPreferenceStorageKey(uid),
			JSON.stringify(createInfoPopupPreference(Boolean(preference?.keepUntilDismissed)))
		);
	} catch {
	}
};

const AUTH_BROADCAST_CHANNEL = 'linkspace-auth';

const mapExtensionSignInError = (errorCode) => {
	if (errorCode === 'missing-email') {
		return 'Open the sign-in link from the same browser where you requested it in the extension.';
	}
	if (errorCode === 'unverified') {
		return 'Please verify your email on the LinkSpace website before using the extension.';
	}
	if (errorCode === 'expired') {
		return 'This sign-in link has expired. Request a new link from the extension.';
	}
	if (errorCode === 'invalid-email') {
		return 'The email for this sign-in link is invalid. Request a new link from the extension.';
	}
	return 'Could not complete extension sign-in. Request a new link from the extension.';
};

const App = () => {
	const [isExtensionHandoffTab] = useState(() => isExtensionEmailLinkUrl() || isExtensionShortSignInUrl());
	const [isEmailLinkTab] = useState(() => isCurrentUrlEmailSignInLink() || isExtensionEmailLinkUrl() || isShortSignInUrl());
	const [emailLinkStatus, setEmailLinkStatus] = useState('processing');
	const [emailLinkAction, setEmailLinkAction] = useState(null);
	const [emailLinkError, setEmailLinkError] = useState(null);
	const [emailLinkNeedsEmail, setEmailLinkNeedsEmail] = useState(false);
	const [showAccountDeletedModal, setShowAccountDeletedModal] = useState(false);
	const [isLoggedIn, setIsLoggedIn] = useState(false);
	const [isAuthReady, setIsAuthReady] = useState(false);
	const [showAddLink, setShowAddLink] = useState(false);
	const [showProfileManager, setShowProfileManager] = useState(false);
	const [showAccountSettings, setShowAccountSettings] = useState(false);
	const [showReportIssue, setShowReportIssue] = useState(false);
	const [showBrowserExtensionGuide, setShowBrowserExtensionGuide] = useState(false);
	const [isEdit, setIsEdit] = useState(false);
	const [userAccount, setUserAccount] = useState(null);
	const [profiles, setProfiles] = useState([ALL_PROFILE]);
	const [profile, setProfile] = useState(ALL_PROFILE);
	const [allCategories, setAllCategories] = useState([]);
	const [allLinks, setAllLinks] = useState([]);
	const [categories, setCategories] = useState([]);
	const [links, setLinks] = useState([]);
	const [errors, setErrors] = useState([]);
	const [infoPopupPreference, setInfoPopupPreference] = useState(createInfoPopupPreference());
	const [isDataLoading, setIsDataLoading] = useState(false);

	const addError = useCallback((text) => {
		setErrors(errs => [
			...errs,
			{ id: uuidv4(), text }
		]);
	}, []);

	const removeError = (id) => {
		setErrors(errs => errs.filter(e => e.id !== id));
	};

	const clearAllErrors = () => {
		setErrors([]);
	};

	const setSessionCookie = (isSessionActive) => {
		if (!isSessionActive) {
			Cookies.remove(AUTH_SESSION_COOKIE, {path: '/'});
			return;
		}

		Cookies.set(AUTH_SESSION_COOKIE, 'active', {
			expires: SESSION_DAYS,
			secure: SECURE_COOKIES,
			sameSite: 'strict',
			path: '/'
		});
	};

	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, async (user) => {
			const hasSessionCookie = Cookies.get(AUTH_SESSION_COOKIE) === 'active';
			if (user && !hasSessionCookie && !isCurrentUrlEmailSignInLink() && !isExtensionEmailLinkUrl() && !isShortSignInUrl()) {
				await firebaseSignOut(auth);
				setSessionCookie(false);
				setIsLoggedIn(false);
				setUserAccount(null);
				setIsAuthReady(true);
				return;
			}

			if (user && hasSessionCookie) {
				if (IS_PROD_ENV && !user.emailVerified && !isCurrentUrlEmailSignInLink() && !isExtensionEmailLinkUrl() && !isShortSignInUrl()) {
					await firebaseSignOut(auth);
					setSessionCookie(false);
					setIsLoggedIn(false);
					setUserAccount(null);
					addError('Please verify your email before logging in.');
					setIsAuthReady(true);
					return;
				}
				setSessionCookie(true);
				setIsLoggedIn(true);
				setUserAccount({
					email: user.email ?? "",
					emailVerified: Boolean(user.emailVerified),
					photoURL: user.photoURL ?? ""
				});
				setInfoPopupPreference(readInfoPopupPreference(user.uid));
				setIsAuthReady(true);
				return;
			}

			setSessionCookie(false);
			setIsLoggedIn(false);
			setUserAccount(null);
			setInfoPopupPreference(createInfoPopupPreference());
			setIsAuthReady(true);
		}, (error) => {
			addError(`Error while observing auth state: ${error.message}`);
			setSessionCookie(false);
			setIsLoggedIn(false);
			setUserAccount(null);
			setInfoPopupPreference(createInfoPopupPreference());
			setIsAuthReady(true);
		});

		return () => unsubscribe();
	}, [addError]);

	useEffect(() => {
		if (globalThis.window === undefined) return undefined;
		if (!isEmailLinkTab) return undefined;
		if (isExtensionHandoffTab) return undefined;
		let cancelled = false;
		(async () => {
			const result = await completeEmailLinkSignIn(null, addError, {
				onSuccess: () => setSessionCookie(true)
			});
			if (cancelled) return;
			if (result.needsEmail) {
				setEmailLinkNeedsEmail(true);
				setEmailLinkStatus('needs-email');
			} else if (result.ok) {
				setEmailLinkNeedsEmail(false);
				setEmailLinkStatus('success');
				setEmailLinkAction(result.action ?? 'signin');
				if (result.action === 'emailChange' || result.action === 'emailRevert') {
					setSessionCookie(false);
				}
			} else {
				setEmailLinkStatus('error');
				setEmailLinkError(result.errorText ?? null);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [addError, isEmailLinkTab, isExtensionHandoffTab]);

	const extensionHandoffResolvedRef = useRef(false);
	const unsubscribeRefs = useRef([]);
	const profileInitializedRef = useRef(false);
	const linksFirstSnapshotRef = useRef(false);
	const categoriesFirstSnapshotRef = useRef(false);

	useEffect(() => {
		if (globalThis.window === undefined) return undefined;
		if (!isExtensionHandoffTab) return undefined;

		let cancelled = false;
		const handleExtensionSignIn = (data) => {
			if (data?.type !== 'LINKSPACE_EXTENSION_SIGNIN') return;
			if (cancelled) return;

			if (data.ok) {
				extensionHandoffResolvedRef.current = true;
				setEmailLinkNeedsEmail(false);
				setEmailLinkStatus('success');
				setEmailLinkAction('extension');
				clearEmailLinkUrlIfPresent();
				return;
			}

			if (extensionHandoffResolvedRef.current) return;

			if (data.error === 'missing-email') {
				setEmailLinkNeedsEmail(true);
				setEmailLinkStatus('needs-email');
				setEmailLinkError(null);
				return;
			}

			setEmailLinkNeedsEmail(false);
			setEmailLinkStatus('error');
			setEmailLinkError(mapExtensionSignInError(data.error));
		};

		const onMessage = (event) => {
			if (event.source !== globalThis.window) return;
			handleExtensionSignIn(event.data);
		};

		const onExtensionSignInEvent = (event) => {
			handleExtensionSignIn(event.detail);
		};

		if (globalThis.__linkspaceExtensionSignInResult) {
			handleExtensionSignIn(globalThis.__linkspaceExtensionSignInResult);
		}

		globalThis.window.addEventListener('message', onMessage);
		globalThis.window.addEventListener('linkspace-extension-signin', onExtensionSignInEvent);
		return () => {
			cancelled = true;
			globalThis.window.removeEventListener('message', onMessage);
			globalThis.window.removeEventListener('linkspace-extension-signin', onExtensionSignInEvent);
		};
	}, [isExtensionHandoffTab]);

	useEffect(() => {
		if (isEmailLinkTab) return undefined;
		if (globalThis.BroadcastChannel === undefined) return undefined;
		const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
		channel.onmessage = (event) => {
			if (event.data?.type === 'ACCOUNT_DELETED') {
				setShowAccountDeletedModal(true);
			}
		};
		return () => channel.close();
	}, [isEmailLinkTab]);

	useEffect(() => {
		if (!isAuthReady) return;

		if (!isLoggedIn) {
			// Tear down any active listeners immediately.
			unsubscribeRefs.current.forEach((fn) => fn());
			unsubscribeRefs.current = [];
			profileInitializedRef.current = false;

			setProfiles([ALL_PROFILE]);
			setProfile(ALL_PROFILE);
			setAllCategories([]);
			setAllLinks([]);
			setCategories([]);
			setLinks([]);
			setShowAddLink(false);
			setShowProfileManager(false);
			setShowAccountSettings(false);
			setShowReportIssue(false);
			setShowBrowserExtensionGuide(false);
			setIsEdit(false);
			setIsDataLoading(false);
			return;
		}

		const uid = auth.currentUser?.uid;
		if (!uid) return;

		// Signal that we are waiting for the first Firestore snapshots.
		linksFirstSnapshotRef.current = false;
		categoriesFirstSnapshotRef.current = false;
		setIsDataLoading(true);

		const markDataLoaded = () => {
			if (linksFirstSnapshotRef.current && categoriesFirstSnapshotRef.current) {
				setIsDataLoading(false);
			}
		};

		// Run storage migration in background — do not block the first render.
		migrateUserStorage(addError).then(() => {
			const user = auth.currentUser;
			if (user) {
				setUserAccount({
					email: user.email ?? "",
					emailVerified: Boolean(user.emailVerified),
					photoURL: user.photoURL ?? ""
				});
			}
		});

		// Links listener — cache returns instantly on repeat visits, then syncs.
		const unsubLinks = subscribeLinks(
			uid,
			(rawLinks) => {
				setAllLinks(rawLinks.map(normalizeLink));
				linksFirstSnapshotRef.current = true;
				markDataLoaded();
			},
			(err) => addError(`Error while observing links: ${err.message}`)
		);

		// Categories listener.
		const unsubCategories = subscribeCategories(
			uid,
			(rawCategories) => {
				setAllCategories(
					rawCategories.map((cat) => normalizeCategory(cat, VIRTUAL_ALL_PROFILE_ID))
				);
				categoriesFirstSnapshotRef.current = true;
				markDataLoaded();
			},
			(err) => addError(`Error while observing categories: ${err.message}`)
		);

		// Profiles listener — restore saved profile from cookie only on first snapshot.
		const unsubProfiles = subscribeProfiles(
			uid,
			(rawProfiles) => {
				const seenIds = new Set([VIRTUAL_ALL_PROFILE_ID]);
				const normalizedFetched = rawProfiles
					.map(normalizeProfile)
					.filter((item) => item?.id && item.profileName !== 'ALL')
					.filter((item) => {
						if (seenIds.has(item.id)) return false;
						seenIds.add(item.id);
						return true;
					});
				const sorted = sortProfiles([ALL_PROFILE, ...normalizedFetched]);
				setProfiles(sorted);

				if (!profileInitializedRef.current) {
					profileInitializedRef.current = true;
					const savedProfileId = Cookies.get('profileId');
					const initial =
						sorted.find((p) => p.id === savedProfileId) ??
						sorted[0] ??
						ALL_PROFILE;
					setProfile(initial);
					Cookies.set('profileId', initial.id, {expires: 7, path: '/'});
				} else {
					// Keep the currently selected profile if it still exists in the updated list.
					setProfile((prev) => sorted.find((p) => p.id === prev?.id) ?? sorted[0] ?? ALL_PROFILE);
				}
			},
			(err) => addError(`Error while observing profiles: ${err.message}`)
		);

		unsubscribeRefs.current = [unsubLinks, unsubCategories, unsubProfiles];

		return () => {
			unsubscribeRefs.current.forEach((fn) => fn());
			unsubscribeRefs.current = [];
			profileInitializedRef.current = false;
		};
	}, [isAuthReady, isLoggedIn, addError]);

	useEffect(() => {
		const activeProfile = profile ?? ALL_PROFILE;
		const filteredLinks = filterLinksByProfile(allLinks, activeProfile, profiles);

		const categoriesForView = filterCategoriesWithLinksForProfile(
			allCategories,
			activeProfile,
			filteredLinks
		);

		const displayCategories = sortCategories(
			categoriesForView.map((category) => ({
				...category,
				idx: getCategoryIdxForProfile(category, activeProfile.id)
			}))
		);

		setLinks(filteredLinks);
		setCategories(displayCategories);
	}, [allCategories, allLinks, profile, profiles]);

	const onRequestEmailSignIn = async (email) => {
		if (!String(email ?? '').trim()) {
			addError('Please enter your email address.');
			return {ok: false};
		}
		return requestEmailSignInLink(email, addError);
	};

	const onCompleteEmailLinkWithEmail = async (email) => {
		if (isExtensionHandoffTab) {
			setEmailLinkStatus('processing');
			setEmailLinkNeedsEmail(false);
			globalThis.window.postMessage({
				type: 'LINKSPACE_EXTENSION_SIGNIN_RETRY',
				email: String(email ?? '').trim()
			}, globalThis.window.location.origin);
			return;
		}

		const result = await completeEmailLinkSignIn(email, addError, {
			onSuccess: () => setSessionCookie(true)
		});
		if (result.ok) {
			setEmailLinkNeedsEmail(false);
			setEmailLinkStatus('success');
			setEmailLinkAction(result.action ?? 'signin');
			if (result.action === 'emailChange' || result.action === 'emailRevert') {
				setSessionCookie(false);
			}
		} else if (!result.needsEmail) {
			setEmailLinkNeedsEmail(false);
			setEmailLinkStatus('error');
			setEmailLinkError(result.errorText ?? null);
		}
	};

	const handleAccountDeletedModalClose = () => {
		setShowAccountDeletedModal(false);
		setSessionCookie(false);
		setIsLoggedIn(false);
		setUserAccount(null);
	};

	const handleLogout = async () => {
		const loggedOut = await logout(addError, {
			onBeforeSignOut: () => {
				// Unsubscribe all Firestore listeners before sign-out so no
				// further reads happen while the cache is being cleared.
				unsubscribeRefs.current.forEach((fn) => fn());
				unsubscribeRefs.current = [];
				profileInitializedRef.current = false;
			},
		});
		if (loggedOut) {
			setIsLoggedIn(false);
			setSessionCookie(false);
			setUserAccount(null);
		}
	};

	const refreshUserAccount = useCallback(() => {
		const user = auth.currentUser;
		if (!user) {
			setUserAccount(null);
			return;
		}
		setUserAccount({
			email: user.email ?? "",
			emailVerified: Boolean(user.emailVerified),
			photoURL: user.photoURL ?? ""
		});
	}, []);

	const handleRequestEmailAddressChange = async (nextEmail) => {
		const updated = await requestEmailAddressChange(nextEmail, addError);
		if (updated) {
			setShowAccountSettings(false);
		}
	};

	const handleUploadAvatar = async (avatarFile) => {
		const photoURL = await uploadUserAvatar(avatarFile, addError);
		if (photoURL) {
			refreshUserAccount();
			return true;
		}
		return false;
	};

	const handleSelectAvatar = async (photoURL) => {
		const selected = await selectUserAvatar(photoURL, addError);
		if (selected) {
			refreshUserAccount();
			return true;
		}
		return false;
	};

	const handleRemoveAvatar = async (photoURL) => {
		const removed = await removeUserAvatar(photoURL, addError);
		if (removed) {
			refreshUserAccount();
			return true;
		}
		return false;
	};

	const handleResendVerification = async () => {
		await resendVerificationEmail(addError);
	};

	const handleDeleteAccount = async () => {
		const userUid = auth.currentUser?.uid;
		const deleted = await deleteCurrentUserAccount(addError);

		if (deleted === 'reauthLinkSent') {
			setShowAccountSettings(false);
			return;
		}

		if (!deleted) return;

		setShowAccountSettings(false);
		setSessionCookie(false);
		setIsLoggedIn(false);
		setUserAccount(null);
		if (userUid) {
			try {
				globalThis.window.localStorage.removeItem(getInfoPopupPreferenceStorageKey(userUid));
			} catch {
			}
		}
	};

	const handleToggleKeepInfoPopups = (keepUntilDismissed) => {
		setInfoPopupPreference((prev) => {
			const next = {
				...prev,
				keepUntilDismissed: Boolean(keepUntilDismissed)
			};
			persistInfoPopupPreference(auth.currentUser?.uid, next);
			return next;
		});
	};

	const handleSubmitBugReport = async ({title, description}) => (
		submitBugReport({title, description}, addError)
	);

	const handleSwitchProfile = () => {
		if (!profiles.length) return;
		const currentIdx = profiles.findIndex((item) => item.id === profile?.id);
		const nextIdx = currentIdx < 0 ? 0 : (currentIdx + 1) % profiles.length;
		const nextProfile = profiles[nextIdx] ?? ALL_PROFILE;
		setProfile(nextProfile);
		Cookies.set("profileId", nextProfile.id, {expires: 7, path: "/"});
	};

	const contextValue = useMemo(() => ({
		addError,
		profiles,
		setProfiles,
		profile,
		setProfile,
		allCategories,
		setAllCategories,
		allLinks,
		setAllLinks,
		isDataLoading,
	}), [profiles, profile, allCategories, allLinks, isDataLoading]);

	return (
		<StateContext.Provider value={contextValue}>
			{showAccountDeletedModal && (
				<div
					role="dialog"
					aria-modal="true"
					aria-labelledby="acct-deleted-title"
					style={{
						position: 'fixed', inset: 0, zIndex: 9999,
						background: 'rgba(0,0,0,0.6)',
						display: 'flex', alignItems: 'center', justifyContent: 'center',
						padding: '16px'
					}}
				>
					<div style={{
						background: '#fff', borderRadius: '16px', padding: '32px 28px',
						maxWidth: '400px', width: '100%', textAlign: 'center',
						boxShadow: '0 20px 48px rgba(0,0,0,0.35)'
					}}>
						<div style={{
							width: 52, height: 52, borderRadius: '50%',
							background: '#fee2e2', display: 'flex',
							alignItems: 'center', justifyContent: 'center',
							margin: '0 auto 18px'
						}}>
							<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
								<polyline points="3 6 5 6 21 6" />
								<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
								<path d="M10 11v6M14 11v6" />
								<path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
							</svg>
						</div>
						<h2 id="acct-deleted-title" style={{margin: '0 0 10px', fontSize: '1.2rem', fontWeight: 700, color: '#1e293b'}}>
							Account deleted
						</h2>
						<p style={{margin: '0 0 24px', color: '#64748b', fontSize: '0.9375rem', lineHeight: 1.55}}>
							Your account and all associated data have been permanently removed.
						</p>
						<button
							onClick={handleAccountDeletedModalClose}
							style={{
								width: '100%', padding: '13px 20px', border: 'none',
								borderRadius: '10px', cursor: 'pointer',
								fontSize: '1rem', fontWeight: 650, color: 'white',
								background: 'linear-gradient(105deg,#38bdf8 0%,#2563eb 48%,#818cf8 100%)'
							}}
						>
							OK
						</button>
					</div>
				</div>
			)}

			{isEmailLinkTab ? (
				<EmailLinkPage
					status={emailLinkStatus}
					action={emailLinkAction}
					onEmailSubmit={onCompleteEmailLinkWithEmail}
					errorText={emailLinkError}
				/>
			) : (
			<div className="app">
				<ErrorList
					errors={errors}
					onRemove={removeError}
					onClearAll={clearAllErrors}
					autoDismissMs={infoPopupPreference.keepUntilDismissed ? null : infoPopupPreference.autoDismissMs}
				/>
				{!isAuthReady || !isLoggedIn ? (
					<LoginWallpaper
						onRequestEmailSignIn={onRequestEmailSignIn}
						onCompleteEmailLinkWithEmail={onCompleteEmailLinkWithEmail}
						emailLinkNeedsEmail={emailLinkNeedsEmail}
						onDismissEmailLinkPrompt={() => {
							setEmailLinkNeedsEmail(false);
							clearEmailLinkUrlIfPresent();
						}}
					/>
				) : (
					<>
						<div className="app-header">
							<Header
								isEdit={isEdit}
								setIsEdit={setIsEdit}
								onLogout={handleLogout}
								onSwitchProfile={handleSwitchProfile}
								setShowAddLink={setShowAddLink}
								showAddLink={showAddLink}
								profile={profile}
								onOpenProfileManager={() => setShowProfileManager(true)}
								onOpenAccountSettings={() => setShowAccountSettings(true)}
								onOpenReportIssue={() => setShowReportIssue(true)}
								onOpenBrowserExtensionGuide={() => setShowBrowserExtensionGuide(true)}
								userPhotoUrl={userAccount?.photoURL}
								userEmail={userAccount?.email}
								defaultAvatarUrl={BUNDLED_DEFAULT_AVATAR}
							/>
						</div>
						<div className="app-body">
						<React.Suspense fallback={null}>
							<AddLink
									open={showAddLink}
									categories={categories}
									setLinks={setLinks}
									setShowAddLink={setShowAddLink}
								/>
						</React.Suspense>
						{showProfileManager && (
							<React.Suspense fallback={null}>
								<ProfileManager
									onClose={() => setShowProfileManager(false)}
								/>
							</React.Suspense>
						)}
						{showAccountSettings && (
							<React.Suspense fallback={null}>
								<AccountSettings
									userAccount={userAccount}
									defaultAvatarUrl={BUNDLED_DEFAULT_AVATAR}
									onClose={() => setShowAccountSettings(false)}
									onRequestEmailAddressChange={handleRequestEmailAddressChange}
									onUploadAvatar={handleUploadAvatar}
									onSelectAvatar={handleSelectAvatar}
									onRemoveAvatar={handleRemoveAvatar}
									onResendVerification={handleResendVerification}
									onDeleteAccount={handleDeleteAccount}
									keepInfoPopupsUntilDismissed={infoPopupPreference.keepUntilDismissed}
									onToggleKeepInfoPopups={handleToggleKeepInfoPopups}
								/>
							</React.Suspense>
						)}
						{showReportIssue && (
							<React.Suspense fallback={null}>
								<ReportIssue
									userAccount={userAccount}
									onClose={() => setShowReportIssue(false)}
									onSubmit={handleSubmitBugReport}
								/>
							</React.Suspense>
						)}
						{showBrowserExtensionGuide && (
							<React.Suspense fallback={null}>
								<BrowserExtensionGuide
									onClose={() => setShowBrowserExtensionGuide(false)}
								/>
							</React.Suspense>
						)}
							<Body
								isEdit={isEdit}
								categories={categories}
								links={links}
								activeProfile={profile}
								addError={addError}
							/>
						</div>
						<div className="app-footer">
							<Footer/>
						</div>
					</>
			)}
		</div>
		)}
	</StateContext.Provider>
	);
};

export default App;
